import { useEffect } from 'react';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { useLocale, useLocalization } from '../../../../../context/Localization/localization';
import { ScrollableBody } from '../../../../Layout/ScrollableBody/ScrollableBody';
import css from './PickUpAtLocker.module.scss';
import { useSetHeaderProps, useSetKioskBannerVisibility } from '../../../../../context/Page/page';
import { Location, useLocation } from 'react-router-dom';
import { useIsLockerOpComplete } from '../IsLockerOpComplete';
import { VehicleReadyQRCode } from '../../../../../controls/VehicleReadyQRCode/VehicleReadyQRCode';
import { GMDialogError } from '../../../../../controls/Dialog';
import { GMSizedIcon } from '../../../../../controls/Icon';
import { AppointmentLoanerOrRentalOption } from '../../../../../API/Enums';
import classNames from 'classnames';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { PickUpAtLockerProps } from './PickUpAtLocker.types';
import { Locales, LocalizationType } from '../../../../../context/Localization/localization.types';
import { GMGrid } from '../../../../../controls/Grid';

/**
 * The screen that gives them instructions on how to finish picking up their vehicle and
 * provides the QR Code they need to scan in order to open the locker. This screen also handles the
 * logic of starting the key return or key exchange.
 *
 * @param onLockerClosed - {@link PickUpAtLockerProps.onLockerClosed onLockerClosed} description.
 * @param onLockerDidntOpen - {@link PickUpAtLockerProps.onLockerDidntOpen onLockerDidntOpen} description.
 * @param appointment - {@link PickUpAtLockerProps.appointment appointment} description.
 */
export const PickUpAtLocker: GoMotoComponent<PickUpAtLockerProps> = ({
    onLockerClosed,
    onLockerDidntOpen,
    appointment,
}) => {
    const locale = useLocale();
    const Localization: LocalizationType = useLocalization();
    const { localization } = useKioskState();
    const showBanner = useSetKioskBannerVisibility();
    const setHeader = useSetHeaderProps();
    const location: Location<unknown> = useLocation();
    const {
        complete,
        error,
    } = useIsLockerOpComplete(appointment.key, appointment.kioskKey, appointment.keySlotNumber ?? 0);
    const isKeyExchange: boolean = appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ||
        appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Rental;

    useEffect(() => {
        setHeader({
            title: Localization.ScanAtLocker,
        });
        showBanner(false);
    }, [setHeader, showBanner, location, Localization.ScanAtLocker, locale]);

    useEffect(() => {
        if (complete) {
            onLockerClosed();
        }
    }, [complete, onLockerClosed]);

    const onErrorClose: VoidFunction = () => {
        onLockerDidntOpen();
    };

    return (
        <ScrollableBody
            proceedButtons={
                <CTAButton
                    styleType={'secondary'}
                    onClick={onLockerDidntOpen}
                    label={Localization.LockerDidntOpen}
                    kioskOptions={{
                        inlineOptions: {
                            sizedIcon: (
                                <GMSizedIcon
                                    iconSize={'custom'}
                                    iconType={'Complete'}
                                    ariaLabel={Localization.CompleteCheckmarkIcon}
                                />
                            ),
                        },
                    }}
                    ariaLabel={Localization.LockerDidntOpen}
                    id={Localization.LockerDidntOpen}
                />
            }
        >
            <GMGrid container>
                <GMGrid
                    container
                    xs={12}
                    className={css.section}
                >
                    <GMGrid xs={12} className={css.title}>
                        <h2>
                            {Localization.ReadyForPickUpEnthusiastic}
                        </h2>
                    </GMGrid>
                    <GMGrid xs={12} className={css.content}>
                        {Localization.PleaseScanTheQRCode}
                    </GMGrid>
                </GMGrid>
                {isKeyExchange ? (
                    <GMGrid
                        container
                        xs={12}
                        className={css.section}
                    >
                        <GMGrid xs={12} className={classNames(css.title)}>
                            <h2 className={css.alert}>
                                {Localization.formatString(
                                    Localization.CarReturn,
                                    appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                        locale === Locales.fr ? Localization.Loaner.toLowerCase() : Localization.Loaner
                                    ) : (
                                        locale === Locales.fr ? Localization.Rental.toLowerCase() : Localization.Rental
                                    ),
                                )}
                            </h2>
                        </GMGrid>
                        <GMGrid xs={12} className={classNames(css.content, css['alert'])}>
                            {Localization.formatString(
                                Localization.BeSureToReturnTheKey,
                                appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                    Localization.Loaner.toLowerCase()
                                ) : (
                                    Localization.Rental.toLowerCase()
                                ),
                            )}
                        </GMGrid>
                    </GMGrid>
                ) : null}
                {localization.location ? (
                    <GMGrid
                        container
                        xs={12}
                        className={css.section}
                    >
                        <GMGrid xs={12} className={css.title}>
                            <h2>
                                {Localization.Location}
                            </h2>
                        </GMGrid>
                        <GMGrid xs={12} className={css.content}>
                            {localization.location}
                        </GMGrid>
                    </GMGrid>
                ) : null}
                {error ? (
                    <GMDialogError
                        ariaLabel={Localization.CheckInError}
                        open={true}
                        onClose={onErrorClose}
                    >
                        {Localization.CheckInError}
                    </GMDialogError>
                ) : (
                    <GMGrid xs={12} className={css.qrCodeSection}>
                        <VehicleReadyQRCode
                            appointmentKey={appointment.key}
                        />
                    </GMGrid>
                )}
                <GMGrid xs={12} className={css.section}>
                    {isKeyExchange ? (
                        Localization.formatString(
                            Localization.WhenLockerOpensPickupReturn,
                            <>
                                {appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                    Localization.Loaner.toLowerCase()
                                ) : (
                                    Localization.Rental.toLowerCase()
                                )}
                            </>,
                            <>
                                {appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? (
                                    Localization.Loaner.toLowerCase()
                                ) : (
                                    Localization.Rental.toLowerCase()
                                )}
                            </>,
                            <b>{Localization.BeSureLockerCloses}</b>,
                        )
                    ) : (
                        Localization.formatString(
                            Localization.WhenLockerOpensPickup,
                            <b>{Localization.BeSureLockerCloses}</b>,
                        )
                    )}
                </GMGrid>
            </GMGrid>
        </ScrollableBody>
    );
};
